import { type QueryClient, queryOptions, useMutation } from '@tanstack/react-query'
import {
  DEFAULT_STREAM_DAYS,
  DEFAULT_STREAM_LOAD_WINDOW,
  STREAM_REMOVE_BATCH,
  streamLoadQuery,
  type CreateLibraryView,
  type HitAnalytics,
  type HitDetail,
  type LibraryView,
  type RemoveStreamHits,
  type RemoveStreamHitsResponse,
  type StreamConfig,
  type StreamConfigInput,
  type StreamHitsResponse,
  type StreamLoadWindow,
  type StreamSectionsResponse,
  type UpdateLibraryView,
} from '@headline/schemas'
import { api } from './api'
import { fail } from './query-fail'
import { applyPendingEdits } from './stream-edits'

/**
 * The stream (CLI-758): configuration, rows and the Library's saved views, per sub-account. Keys
 * nest under `['stream', subaccountId]` so one invalidation after a configuration change
 * refreshes the rows and the views together — the rows are a function of the configuration.
 */

/** How a load window reads in a sentence: "the last 30 days", "1–30 Sep 2026". */
export function streamWindowLabel(window: StreamLoadWindow): string {
  if (window.kind === 'preset')
    return window.days === 365 ? 'the last year' : `the last ${window.days} days`
  const day = (value: string) =>
    new Date(`${value}T00:00:00Z`).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
  if (window.from && window.to) return `${day(window.from)} to ${day(window.to)}`
  if (window.from) return `the days since ${day(window.from)}`
  if (window.to) return `the year up to ${day(window.to)}`
  return `the last ${DEFAULT_STREAM_DAYS} days`
}

/** What a page renders before the rows arrive, or where they cannot be read. */
export const EMPTY_STREAM_READ: StreamHitsResponse = {
  hits: [],
  hasMore: false,
  coveredFrom: new Date(0).toISOString(),
  cached: true,
  computedAt: null,
  mode: 'reports',
  topicsStamped: true,
  prior: null,
  window: { from: new Date(0).toISOString(), to: new Date(0).toISOString() },
}

export function streamConfigQuery(subaccountId: string) {
  return queryOptions({
    queryKey: ['stream', subaccountId, 'config'],
    queryFn: async (): Promise<StreamConfig> => {
      const res = await api.api.streams[':subaccountId'].config.$get({ param: { subaccountId } })
      if (!res.ok) return fail(res, 'Failed to load the stream configuration')
      return res.json()
    },
  })
}

/**
 * The analytics build's sections beside the articles — echo chains, narratives, whitespace,
 * jurisdictions, whatever this client's build writes — as the reader kept them. One per
 * sub-account (the build has no window); the cache behind it refreshes hourly, so five minutes
 * is fresh enough and a reopened page does not read it again.
 */
export function streamSectionsQuery(subaccountId: string) {
  return queryOptions({
    queryKey: ['stream', subaccountId, 'sections'] as const,
    queryFn: async (): Promise<StreamSectionsResponse> => {
      const res = await api.api.streams[':subaccountId'].sections.$get({ param: { subaccountId } })
      if (!res.ok) return fail(res, 'Failed to load the analytics build’s sections')
      return res.json()
    },
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,
  })
}

/**
 * The rows a page shows: the window its date selection names, applied on the SERVER (at most
 * `STREAM_HITS_CAP` rows of it, newest first), then filtered client-side by facet. The window is
 * part of the key in a bounded way: a relative selection loads one of three presets (30 / 90 /
 * 365 days — 24h and 7d are cut out of the 30-day load in the browser), and a custom range is
 * keyed by its two dates.
 */
export function streamHitsQuery(
  subaccountId: string,
  window: StreamLoadWindow = DEFAULT_STREAM_LOAD_WINDOW,
) {
  return queryOptions({
    queryKey:
      window.kind === 'preset'
        ? (['stream', subaccountId, 'hits', window.days] as const)
        : (['stream', subaccountId, 'hits', 'range', window.from ?? '', window.to ?? ''] as const),
    queryFn: async (): Promise<StreamHitsResponse> => {
      const res = await api.api.streams[':subaccountId'].hits.$get({
        param: { subaccountId },
        query: streamLoadQuery(window),
      })
      if (!res.ok) return fail(res, 'Failed to load the stream')
      // This browser's article edits the cache has not built yet (CLI-1021).
      return applyPendingEdits(subaccountId, await res.json())
    },
    // Up to 25,000 rows (~14 MB of JSON) per window, and switching the range mints a second
    // entry: an unused window is dropped after a minute, not held for the day. The cache behind
    // it refreshes hourly.
    gcTime: 60_000,
    staleTime: 60_000,
  })
}

/**
 * One hit's full record, by id — what the Library's article panel opens (CLI-758 follow-up). A
 * live by-id read of the analytics backend behind `GET /api/streams/:subaccountId/hits/:hitId`, not
 * the cached listing: the richer record behind the row. `content` asks for the heavy body (full
 * text / transcript) and keys its own cache entry, so the lighter metadata read is never evicted by
 * it. Kept a few minutes — a panel reopened in that window does not re-fetch.
 */
export function hitDetailQuery(subaccountId: string, hitId: string, content = false) {
  return queryOptions({
    queryKey: ['stream', subaccountId, 'hit', hitId, content ? 'full' : 'lite'] as const,
    queryFn: async (): Promise<HitDetail> => {
      const res = await api.api.streams[':subaccountId'].hits[':hitId'].$get({
        param: { subaccountId, hitId },
        query: { content: content ? 'true' : 'false' },
      })
      if (!res.ok) return fail(res, 'Failed to load the article')
      return res.json()
    },
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,
  })
}

/**
 * One hit's sentiment analysis (`GET …/hits/:hitId/analytics`), fetched in PARALLEL with
 * `hitDetailQuery` — the panel fires both on open, so the score lands next to the stream's
 * sentiment without waiting on the detail. Its own cache entry and loading state; a hit the
 * analyzer hasn't scored (404) surfaces as a thrown error the panel treats as "no score".
 */
export function hitAnalyticsQuery(subaccountId: string, hitId: string) {
  return queryOptions({
    queryKey: ['stream', subaccountId, 'hit', hitId, 'analytics'] as const,
    queryFn: async (): Promise<HitAnalytics> => {
      const res = await api.api.streams[':subaccountId'].hits[':hitId'].analytics.$get({
        param: { subaccountId, hitId },
      })
      if (!res.ok) return fail(res, 'Failed to load sentiment analysis')
      return res.json()
    },
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,
    // A hit with no score (404) is normal — don't hammer it on every reopen.
    retry: false,
  })
}

export function libraryViewsQuery(subaccountId: string) {
  return queryOptions({
    queryKey: ['stream', subaccountId, 'views'],
    queryFn: async (): Promise<LibraryView[]> => {
      const res = await api.api.streams[':subaccountId'].views.$get({ param: { subaccountId } })
      if (!res.ok) return fail(res, 'Failed to load the saved views')
      return (await res.json()).views
    },
  })
}

export function useReplaceStreamConfig(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: StreamConfigInput): Promise<StreamConfig> => {
      const res = await api.api.streams[':subaccountId'].config.$put({
        param: { subaccountId },
        json: input,
      })
      if (!res.ok) return fail(res, 'Failed to save the stream configuration')
      return res.json()
    },
    onSuccess: (config) => {
      queryClient.setQueryData(streamConfigQuery(subaccountId).queryKey, config)
      return queryClient.invalidateQueries({ queryKey: ['stream', subaccountId, 'hits'] })
    },
  })
}

const invalidateViews = (queryClient: QueryClient, subaccountId: string) =>
  queryClient.invalidateQueries({ queryKey: libraryViewsQuery(subaccountId).queryKey })

export function useCreateLibraryView(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: CreateLibraryView): Promise<LibraryView> => {
      const res = await api.api.streams[':subaccountId'].views.$post({
        param: { subaccountId },
        json: input,
      })
      if (!res.ok) return fail(res, 'Failed to save the view')
      return res.json()
    },
    onSuccess: () => invalidateViews(queryClient, subaccountId),
  })
}

export function useUpdateLibraryView(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async ({
      id,
      ...patch
    }: { id: string } & UpdateLibraryView): Promise<LibraryView> => {
      const res = await api.api.streams[':subaccountId'].views[':id'].$patch({
        param: { subaccountId, id },
        json: patch,
      })
      if (!res.ok) return fail(res, 'Failed to update the view')
      return res.json()
    },
    onSuccess: () => invalidateViews(queryClient, subaccountId),
  })
}

export function useDeleteLibraryView(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.api.streams[':subaccountId'].views[':id'].$delete({
        param: { subaccountId, id },
      })
      if (!res.ok) return fail(res, 'Failed to delete the view')
    },
    onSuccess: () => invalidateViews(queryClient, subaccountId),
  })
}

/**
 * The read without the rows a removal just took out. The table empties of them at once; the
 * server's next read drops them for real (`hit_deletions`, read at request time). The same object
 * comes back when nothing in it was named, so an untouched window does not re-render.
 */
export function withoutHits(
  read: StreamHitsResponse,
  hitIds: Iterable<string>,
): StreamHitsResponse {
  const gone = new Set(hitIds)
  if (gone.size === 0) return read
  const hits = read.hits.filter((hit) => !gone.has(hit.id))
  return hits.length === read.hits.length ? read : { ...read, hits }
}

/**
 * The Library's bulk Remove (CLI-625), in batches of `STREAM_REMOVE_BATCH`: Select all can name
 * every row of the window, and the route takes at most that many per call. Batches run one after
 * another; a failure part-way stops there, and the rows already recorded stay recorded — the next
 * read shows them gone, the error says the rest were not removed.
 */
export async function removeStreamHits(
  subaccountId: string,
  input: RemoveStreamHits,
): Promise<RemoveStreamHitsResponse> {
  const recorded: RemoveStreamHitsResponse['recorded'] = []
  const already: RemoveStreamHitsResponse['already'] = []
  for (let at = 0; at < input.hitIds.length; at += STREAM_REMOVE_BATCH) {
    const res = await api.api.streams[':subaccountId'].removals.$post({
      param: { subaccountId },
      json: { ...input, hitIds: input.hitIds.slice(at, at + STREAM_REMOVE_BATCH) },
    })
    if (!res.ok) {
      return fail(
        res,
        at === 0
          ? 'Failed to remove the articles'
          : `Failed after removing the first ${at} articles; the rest were not removed`,
      )
    }
    const page = await res.json()
    recorded.push(...page.recorded)
    already.push(...page.already)
  }
  return { recorded, already }
}

export function useRemoveStreamHits(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: (input: RemoveStreamHits) => removeStreamHits(subaccountId, input),
    onSuccess: (_result, input) => {
      // Every loaded window loses the rows now, and is marked stale so the next mount or focus
      // reads the server's truth — not refetched here: a window is up to 25,000 rows.
      queryClient.setQueriesData<StreamHitsResponse>(
        { queryKey: ['stream', subaccountId, 'hits'] },
        (read) => (read ? withoutHits(read, input.hitIds) : read),
      )
      return queryClient.invalidateQueries({
        queryKey: ['stream', subaccountId, 'hits'],
        refetchType: 'none',
      })
    },
  })
}
